import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { Box, TextButton, Typography } from 'seagull';
import { useIoContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { answerFreshnessText, GetAnalystAgentPaneTID } from '../AnalystAgentPane.utils';
import { useAnalystEntityResolver } from '../hooks/useAnalystEntityResolver';
import { humanizeColumnHeader } from '../resolution/columnHeader';
import styles from './AnalystResultTableMessage.module.scss';
import { analystCsvFilename, displayedCount, displayedRows, formatCell, hasResultData, humanizeEnumCell, renderCellText, resolveColumnAlignments, shouldRenderTable, shouldShowTruncationBanner, toCsvRows } from './AnalystResultTableMessage.utils';
export const AnalystResultTableMessage = (param)=>{
    let { messageId, part } = param;
    const { t } = useLocaleKeys('analystAgent');
    const { getNow, browser } = useIoContext();
    const { resolve } = useAnalystEntityResolver();
    const { AnalystAgentPaneTestIds } = useMemo(()=>GetAnalystAgentPaneTID(messageId), [
        messageId
    ]);
    const { columns = [], rows = [], truncated = false, row_count = 0 } = part;
    const headerFallbackLabels = {
        client: t.resultTable.entityRefFallback.client(),
        agreement: t.resultTable.entityRefFallback.agreement()
    };
    const handleDownload = ()=>browser.downloadCsv(toCsvRows(columns, rows, resolve, headerFallbackLabels), analystCsvFilename(getNow(), truncated));
    const cellLabels = {
        nullPlaceholder: t.resultTable.nullPlaceholder(),
        booleanYes: t.resultTable.booleanYes(),
        booleanNo: t.resultTable.booleanNo()
    };
    const columnAlignments = resolveColumnAlignments(columns);
    if (!hasResultData(columns, rows, row_count)) return null;
    const freshnessLine = /*#__PURE__*/ _jsx(Typography, {
        skin: "secondary",
        variant: "paragraph3",
        testId: AnalystAgentPaneTestIds.ResultTableFreshness(),
        children: answerFreshnessText(part.last_synced_at, getNow(), {
            synced: (relative)=>t.resultTable.freshness({
                    when: relative
                }),
            unavailable: t.resultTable.freshnessUnavailable()
        })
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            shouldRenderTable(columns, rows, row_count) && /*#__PURE__*/ _jsxs(Box, {
                className: styles.root,
                testId: AnalystAgentPaneTestIds.ResultTable(),
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        className: styles.scrollRegion,
                        role: "region",
                        "aria-label": t.resultTable.tableLabel(),
                        tabIndex: 0,
                        children: /*#__PURE__*/ _jsxs("table", {
                            className: styles.table,
                            children: [
                                /*#__PURE__*/ _jsx("thead", {
                                    children: /*#__PURE__*/ _jsx("tr", {
                                        children: columns.map((column, colIndex)=>{
                                            const headerLabel = humanizeColumnHeader(column, headerFallbackLabels);
                                            return /*#__PURE__*/ _jsx("th", {
                                                scope: "col",
                                                className: cn(styles.headerCell, {
                                                    [styles.stickyColumn]: colIndex === 0
                                                }),
                                                title: headerLabel,
                                                "data-align": columnAlignments[colIndex],
                                                "data-testid": AnalystAgentPaneTestIds.ResultTableHeader(colIndex),
                                                children: /*#__PURE__*/ _jsx(Typography, {
                                                    className: styles.cellText,
                                                    weight: "light",
                                                    variant: "paragraph3",
                                                    children: headerLabel
                                                })
                                            }, colIndex);
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx("tbody", {
                                    children: displayedRows(rows).map((row, rowIndex)=>/*#__PURE__*/ _jsx("tr", {
                                            className: cn({
                                                [styles.striped]: rowIndex % 2 === 1
                                            }),
                                            children: row.map((cell, colIndex)=>{
                                                var _columnAlignments_colIndex;
                                                const column = columns[colIndex];
                                                const text = humanizeEnumCell(renderCellText(formatCell(cell, column, resolve), cellLabels), column);
                                                return /*#__PURE__*/ _jsx("td", {
                                                    className: cn(styles.bodyCell, {
                                                        [styles.stickyColumn]: colIndex === 0
                                                    }),
                                                    title: text,
                                                    "data-align": (_columnAlignments_colIndex = columnAlignments[colIndex]) !== null && _columnAlignments_colIndex !== void 0 ? _columnAlignments_colIndex : 'left',
                                                    "data-testid": AnalystAgentPaneTestIds.ResultTableCell(rowIndex, colIndex),
                                                    children: /*#__PURE__*/ _jsx(Typography, {
                                                        className: styles.cellText,
                                                        variant: "paragraph3",
                                                        children: text
                                                    })
                                                }, colIndex);
                                            })
                                        }, rowIndex))
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Box, {
                        className: styles.footer,
                        children: [
                            shouldShowTruncationBanner(rows, truncated) && /*#__PURE__*/ _jsx(Typography, {
                                skin: "secondary",
                                variant: "paragraph3",
                                testId: AnalystAgentPaneTestIds.ResultTableBanner(),
                                children: t.resultTable.truncationBanner({
                                    count: displayedCount(rows)
                                })
                            }),
                            freshnessLine,
                            /*#__PURE__*/ _jsx(TextButton, {
                                bi: {
                                    name: 'analyst_result_table_download_csv'
                                },
                                variant: "paragraph3",
                                weight: "light",
                                testId: AnalystAgentPaneTestIds.ResultTableDownloadButton(),
                                onClick: handleDownload,
                                children: t.resultTable.downloadCsv()
                            })
                        ]
                    })
                ]
            }),
            !shouldRenderTable(columns, rows, row_count) && /*#__PURE__*/ _jsx(Box, {
                className: styles.standaloneFreshness,
                children: freshnessLine
            })
        ]
    });
};
